import { createClient } from "@/utils/supabase/client";

const supabase = createClient();

const useSpaceStore = (set, get) => ({
  spaces: [],
  spaceContent: {}, // Cache for individual space content, keyed by slug
  modifiedSpaces: new Set(), // Track which spaces have been modified

  // Add spaces management
  setSpaces: (spaces) => set({ spaces }),

  clearSpaces: () => set({ spaces: [] }),

  // Mark a space as modified (needs refresh)
  markSpaceAsModified: (slug) => {
    set((state) => {
      const modifiedSpaces = new Set(state.modifiedSpaces);
      modifiedSpaces.add(slug);
      return { modifiedSpaces };
    });
  },

  // Clear modified status for a space
  clearModifiedStatus: (slug) => {
    set((state) => {
      const modifiedSpaces = new Set(state.modifiedSpaces);
      modifiedSpaces.delete(slug);
      return { modifiedSpaces };
    });
  },

  // Check if a space needs refreshing
  doesSpaceNeedRefresh: (slug) => {
    return get().modifiedSpaces.has(slug);
  },

  // Get space content from cache or fetch if needed
  getSpaceContent: async (slug, forceRefresh = false) => {
    const store = get();
    const cachedContent = store.spaceContent[slug];
    const needsRefresh =
      forceRefresh || store.doesSpaceNeedRefresh(slug) || !cachedContent;

    if (needsRefresh) {
      try {
        const session = get().session;
        const { data: spaceContent, error: spaceError } = await supabase.rpc(
          "get_space_content",
          {
            space_slug: slug,
            user_id: session?.user?.id || null,
          }
        );

        if (spaceError || !spaceContent?.[0]?.space_data) {
          throw spaceError || new Error("No space data returned");
        }

        // Store in cache
        const spaceData = spaceContent[0];
        set((state) => ({
          spaceContent: {
            ...state.spaceContent,
            [slug]: spaceData,
          },
        }));

        // Clear modified flag
        store.clearModifiedStatus(slug);

        return spaceData;
      } catch (error) {
        console.error("Error fetching space content:", error);
        return null;
      }
    }

    return cachedContent;
  },

  // Cache space content directly
  cacheSpaceContent: (slug, content) => {
    set((state) => ({
      spaceContent: {
        ...state.spaceContent,
        [slug]: content,
      },
    }));
  },

  // Fetch user spaces
  fetchUserSpaces: async () => {
    try {
      const session = get().session;
      if (!session?.user?.id) {
        set({ spaces: [] });
        return;
      }

      const { data: spaces, error } = await supabase
        .from("spaces")
        .select("id, title, slug, created_at")
        .eq("user_id", session.user.id)
        .order("created_at", { ascending: false });

      if (error) throw error;

      set({ spaces: spaces || [] });

      return spaces;
    } catch (error) {
      console.error("Error fetching spaces:", error);
      get().showToast({
        title: "Error",
        description: "Failed to load your spaces",
        variant: "destructive",
      });
      return [];
    }
  },

  // Create initial space (with sign in if needed)
  createInitialSpace: async ({
    slug,
    type,
    tags = [],
    expirationHours,
    referralCode,
  }) => {
    try {
      // Get the current session
      const {
        data: { session },
      } = await supabase.auth.getSession();
      if (!session) {
        throw new Error("No active session");
      }

      // Prepare the payload
      const payload = {
        slug,
        spaceType: type,
        tags,
        expirationHours,
      };

      console.log(payload);

      // Call the edge function
      const response = await fetch(
        `${process.env.NEXT_PUBLIC_SUPABASE_URL}/functions/v1/create-space`,
        {
          method: "POST",
          headers: {
            Authorization: `Bearer ${session.access_token}`,
            "Content-Type": "application/json",
          },
          body: JSON.stringify(payload),
        }
      );

      if (!response.ok) {
        const error = await response.json();

        // If space creation failed and we have a referral code, clean up the referral
        if (referralCode) {
          try {
            // Call the clean-up endpoint to remove the referral
            await fetch(
              `${process.env.NEXT_PUBLIC_SUPABASE_URL}/functions/v1/cleanup-referral`,
              {
                method: "POST",
                headers: {
                  Authorization: `Bearer ${session.access_token}`,
                  "Content-Type": "application/json",
                },
                body: JSON.stringify({
                  referralCode,
                  userId: session.user.id,
                }),
              }
            );
          } catch (cleanupError) {
            console.error("Error cleaning up referral:", cleanupError);
          }
        }

        throw new Error(error.message || "Failed to create space");
      }

      const data = await response.json();

      // Update local state with the new space
      set((state) => ({
        spaces: [
          ...(state.spaces || []),
          {
            id: data.space_id,
            slug,
            type,
            draft_id: data.draft_id,
          },
        ],
      }));

      return {
        success: true,
        spaceId: data.space_id,
        draftId: data.draft_id,
      };
    } catch (error) {
      console.error("Error creating space:", error);
      return {
        success: false,
        error: error.message || "Failed to create space",
      };
    }
  },

  // Create space
  createSpace: async ({ slug, type, tags, title }) => {
    try {
      const session = get().session;
      if (!session) throw new Error("Not authenticated");

      // Call the create_space function
      const { data, error } = await supabase.rpc("create_space", {
        p_user_id: session.user.id,
        p_slug: slug,
        p_title: title || `My ${type} Space`,
        p_tags: tags,
      });

      if (error) throw error;

      // Fetch updated spaces list
      await get().fetchUserSpaces();

      get().showToast({
        title: "Success",
        description: "Space created successfully",
        variant: "success",
      });

      return {
        success: true,
        spaceId: data[0].space_id,
        draftId: data[0].draft_id,
        slug,
        error: null,
      };
    } catch (error) {
      get().showToast({
        title: "Error",
        description: error.message || "Failed to create space",
        variant: "destructive",
      });
      return { success: false, spaceId: null, error: error.message };
    }
  },
});

export default useSpaceStore;
